import React from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { SeedlingIcon, UsersIcon, CrownIcon, BookIcon, ShieldIcon } from '../Icons';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadMessageCount } from '../../hooks/queries/useUnreadMessageCount';
import { PILLAR_CONFIG, ADMIN_PILLAR, getActivePillar } from './navConfig';

const PILLAR_ICONS: Record<string, React.FC<{ size?: number; className?: string }>> = {
  SeedlingIcon,
  UsersIcon,
  CrownIcon,
  BookIcon,
  ShieldIcon,
};

function HomeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

const NAV_ITEM_CLS = 'flex-1 flex flex-col items-center gap-1 pt-2 no-underline';
// Accent law §2.1: amber is the only interactive accent (bottom nav included);
// §2.6 forbids the previous hardcoded shadow palette of the navbar tokens.
const ACTIVE_COLOR = 'var(--theme-primary)';
const INACTIVE_COLOR = 'var(--theme-navbar-text)';

export const MobileBottomNav: React.FC = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const activePillar = getActivePillar(pathname);
  const isHome = pathname === '/helm' || pathname === '/';
  // Notification-prominence follow-up (2026-08-15): DMs are one tab deep
  // inside Connect, so an unread badge one level up (here) is what actually
  // makes them prominent — a badge buried under Connect > Messages gives no
  // reason to tap in and check.
  const unreadMessageCount = useUnreadMessageCount();
  // Five fixed slots (Home + the four pillars) for regular users; super_admins
  // get a sixth Admin slot.
  const pillars = user?.role === 'super_admin' ? [...PILLAR_CONFIG, ADMIN_PILLAR] : PILLAR_CONFIG;

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-[90] flex border-t"
      style={{
        background: 'var(--theme-navbar-bg)',
        borderColor: 'var(--theme-navbar-border)',
        paddingBottom: 'max(14px, env(safe-area-inset-bottom))',
      }}
      aria-label="Bottom navigation"
    >
      <Link
        to="/helm"
        className={NAV_ITEM_CLS}
        style={{ color: isHome ? ACTIVE_COLOR : INACTIVE_COLOR }}
      >
        <HomeIcon />
        <span style={{ fontSize: '10px', fontWeight: 500 }}>Home</span>
      </Link>

      {pillars.map((pillar) => {
        const isActive = activePillar === pillar.id;
        const Icon = PILLAR_ICONS[pillar.icon];
        const badgeCount = pillar.id === 'connect' ? unreadMessageCount : 0;
        return (
          <NavLink
            key={pillar.id}
            to={pillar.baseRoute}
            className={NAV_ITEM_CLS}
            style={{ color: isActive ? ACTIVE_COLOR : INACTIVE_COLOR }}
          >
            <span className="relative">
              {Icon ? <Icon size={20} /> : null}
              {badgeCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center bg-accent text-white"
                  aria-label={`${badgeCount} unread message${badgeCount === 1 ? '' : 's'}`}
                >
                  {badgeCount > 9 ? '9+' : badgeCount}
                </span>
              )}
            </span>
            <span style={{ fontSize: '10px', fontWeight: 500 }}>{pillar.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};
